import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery, useLogoutMutation } from "../../store/api/authApi";
import Icon, { type IconName } from "../AppIcon";
import NotificationBell from "../notifications/NotificationBell";
import Button from "./Button";

interface NavigationItem {
	label: string;
	path: string;
	icon: IconName;
	description: string;
}

const navigationItems: NavigationItem[] = [
	{
		label: "Dashboard",
		path: "/admin-dashboard",
		icon: "LayoutDashboard",
		description: "Overview and analytics",
	},
	{
		label: "Analytics",
		path: "/analytics-dashboard",
		icon: "BarChart3",
		description: "Detailed analytics & insights",
	},
	{
		label: "Properties",
		path: "/property-management",
		icon: "Building2",
		description: "Manage property portfolio",
	},
	{
		label: "Tenants",
		path: "/tenant-management",
		icon: "Users",
		description: "Tenant relationships",
	},
	{
		label: "User Management",
		path: "/user-management",
		icon: "UserPlus",
		description: "Add users & assign roles",
	},
	{
		label: "Leases",
		path: "/lease-management",
		icon: "FileText",
		description: "Lease agreements",
	},
	{
		label: "Payment Accounts",
		path: "/payment-accounts-management",
		icon: "Wallet",
		description: "Bank account details",
	},
	{
		label: "Payment Management",
		path: "/payment-proof-management",
		icon: "FileCheck",
		description: "Review initial payment proofs",
	},
	{
		label: "Lease Payments",
		path: "/payment-management",
		icon: "CreditCard",
		description: "Ongoing rental payments",
	},
	{
		label: "Maintenance",
		path: "/maintenance-management",
		icon: "Wrench",
		description: "Maintenance requests",
	},
	{
		label: "Sales",
		path: "/sales-management",
		icon: "TrendingUp",
		description: "Property sales management",
	},
	{
		label: "Viewings",
		path: "/property-viewing-management",
		icon: "Calendar",
		description: "Viewing coordination",
	},
	{
		label: "Messages",
		path: "/client-messaging-center",
		icon: "MessageSquare",
		description: "Client communication",
	},
	{
		label: "Announcements",
		path: "/announcement-management",
		icon: "Megaphone",
		description: "System announcements",
	},
	{
		label: "Property Requests",
		path: "/property-requests-management",
		icon: "FileCheck",
		description: "Lease and buy requests",
	},
	{
		label: "Technician",
		path: "/technician-dashboard",
		icon: "Wrench",
		description: "Task management",
	},
];

const Sidebar: React.FC = () => {
	const [isCollapsed, setIsCollapsed] = useState(false); // Desktop sidebar collapsed state
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile drawer state
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const profileRef = useRef<HTMLDivElement | null>(null);

	// Fetch user profile data
	const accessToken = tokenStorage.getAccessToken();
	const { data: user } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);
	const [logout] = useLogoutMutation();

	const handleNavigation = (path: string) => {
		navigate(path);
		// Auto-collapse sidebar on mobile after navigation
		setIsMobileMenuOpen(false);
	};

	const isActivePath = (path: string) => location?.pathname === path;

	const handleLogout = async () => {
		try {
			await logout().unwrap();
			tokenStorage.clearTokens();
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
			// Clear tokens anyway and redirect
			tokenStorage.clearTokens();
			navigate("/login");
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				profileRef.current &&
				!profileRef.current.contains(event.target as Node)
			) {
				setIsProfileOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return (
		<>
			{/* Desktop Sidebar */}
			<aside
				className={`fixed left-0 top-0 h-screen bg-card border-r border-border sidebar-shadow z-1000 smooth-transition flex flex-col ${
					isCollapsed ? "w-16" : "w-60"
				} hidden lg:block`}
			>
				{/* Logo Section */}
				<div className="h-16 flex items-center px-4 border-b border-border flex-shrink-0">
					{!isCollapsed ? (
						<div className="flex items-center space-x-3">
							<img
								onClick={() => navigate("/")}
								src="/assets/images/desktop_logo.png"
								alt="Dofarahub Logo"
								className="h-8 w-auto object-contain cursor-pointer"
							/>
						</div>
					) : (
						<div className="flex items-center justify-center mx-auto">
							<img
								onClick={() => navigate("/")}
								className="h-8 w-auto object-contain cursor-pointer"
								src="/assets/images/desktop_logo.png"
								alt="Dofarahub Logo"
							/>
						</div>
					)}
				</div>

				{/* Navigation */}
				<nav
					className="overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
					style={{ maxHeight: "calc(100vh - 8rem)" }}
				>
					<div className="space-y-2 p-4">
						{navigationItems?.map((item) => (
							<button
								key={item?.path}
								onClick={() => handleNavigation(item?.path)}
								className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium smooth-transition ${
									isActivePath(item?.path)
										? "bg-primary text-primary-foreground"
										: "text-muted-foreground hover:text-foreground hover:bg-muted"
								}`}
								title={isCollapsed ? item?.label : ""}
							>
								<Icon
									name={item?.icon}
									size={20}
									className={
										isActivePath(item?.path) ? "text-primary-foreground" : ""
									}
								/>
								{!isCollapsed && (
									<div className="flex-1 text-left">
										<div className="font-medium">{item?.label}</div>
										<div className="text-xs opacity-75">
											{item?.description}
										</div>
									</div>
								)}
							</button>
						))}
					</div>
				</nav>

				{/* Collapse Toggle */}
				<div className="p-4 border-t border-border flex-shrink-0">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setIsCollapsed(!isCollapsed)}
						className="w-full justify-center"
					>
						<Icon
							name={isCollapsed ? "ChevronRight" : "ChevronLeft"}
							size={16}
						/>
						{!isCollapsed && <span className="ml-2">Collapse</span>}
					</Button>
				</div>
			</aside>
			{/* Mobile Sidebar Overlay */}
			<div className="lg:hidden">
				{/* Mobile Header with Menu Button */}
				<div className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-1100">
					<div className="flex items-center space-x-3">
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						>
							<Icon name="Menu" size={20} />
						</Button>
						<div className="flex items-center space-x-2">
							<button
								onClick={() => navigate("/")}
								className="flex items-center space-x-2 hover:opacity-80 smooth-transition"
							>
								<Icon name="ArrowLeft" size={10} className="text-foreground" />
								<span className="text-sm font-medium text-foreground">
									Back to Home
								</span>
							</button>
						</div>
					</div>

					{/* Mobile Actions */}
					<div className="flex items-center space-x-2">
						{/* Mobile Notification Bell */}
						<NotificationBell />

						{/* Mobile Profile Dropdown */}
						<div className="relative" ref={profileRef}>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => setIsProfileOpen(!isProfileOpen)}
							>
								<div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
									<Icon name="User" size={16} />
								</div>
							</Button>

							{isProfileOpen && user && (
								<div className="absolute right-0 top-12 w-56 bg-popover border border-border rounded-lg modal-shadow z-[9998] animate-scale-in">
									<div className="p-4 border-b border-border">
										<p className="font-medium text-popover-foreground">
											{`${user.first_name} ${user.last_name}`}
										</p>
										<p className="text-sm text-muted-foreground truncate">
											{user.email}
										</p>
										<span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full capitalize">
											{user.role}
										</span>
									</div>

									<div className="py-2">
										<button
											onClick={() => {
												setIsProfileOpen(false);
												navigate("/profile");
											}}
											className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
										>
											<Icon name="User" size={16} />
											<span>Profile Settings</span>
										</button>
										<button
											onClick={() => {
												setIsProfileOpen(false);
												navigate("/settings");
											}}
											className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
										>
											<Icon name="Settings" size={16} />
											<span>Account Settings</span>
										</button>
										<button
											onClick={() => {
												setIsProfileOpen(false);
												navigate("/help");
											}}
											className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
										>
											<Icon name="HelpCircle" size={16} />
											<span>Help & Support</span>
										</button>
									</div>

									<div className="border-t border-border py-2">
										<button
											onClick={handleLogout}
											className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 smooth-transition flex items-center space-x-2"
										>
											<Icon name="LogOut" size={16} />
											<span>Sign Out</span>
										</button>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Mobile Sidebar Drawer */}
				{isMobileMenuOpen && (
					<>
						{/* Backdrop */}
						<div
							className="fixed inset-0 bg-black/50 z-1100 animate-fade-in"
							onClick={() => setIsMobileMenuOpen(false)}
						/>

						{/* Drawer */}
						<aside className="fixed left-0 top-0 h-screen w-64 bg-card border-r border-border sidebar-shadow z-1200 animate-slide-in flex flex-col">
							{/* Logo Section */}
							<div className="h-16 flex items-center px-4 border-b border-border flex-shrink-0">
								<div className="flex items-center space-x-3">
									<img
										src="/assets/images/desktop_logo.png"
										alt="Dofarahub Logo"
										className="h-8 w-auto object-contain"
									/>
								</div>
							</div>

							{/* Navigation */}
							<nav
								className="overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
								style={{ maxHeight: "calc(100vh - 4rem)" }}
							>
								<div className="space-y-2 p-4">
									{navigationItems?.map((item) => (
										<button
											key={item?.path}
											onClick={() => {
												handleNavigation(item?.path);
												setIsMobileMenuOpen(false);
											}}
											className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium smooth-transition ${
												isActivePath(item?.path)
													? "bg-primary text-primary-foreground"
													: "text-muted-foreground hover:text-foreground hover:bg-muted"
											}`}
										>
											<Icon
												name={item?.icon}
												size={20}
												className={
													isActivePath(item?.path)
														? "text-primary-foreground"
														: ""
												}
											/>
											<div className="flex-1 text-left">
												<div className="font-medium">{item?.label}</div>
												<div className="text-xs opacity-75">
													{item?.description}
												</div>
											</div>
										</button>
									))}
								</div>
							</nav>
						</aside>
					</>
				)}
			</div>
		</>
	);
};

export default Sidebar;
